import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React__default, { useRef, useMemo } from 'react';
import { styled } from 'styled-components';
import { useChatbot } from '../../hooks/useChatbot.js';
import TypingDots from '../TypingDots.js';
import ChatbotError from './ChatbotError.js';
import ChatbotMessage, { FabricChatbotAvatar } from './ChatbotMessage.js';
import { AssistantContextSubType, AssistantContextType } from '../../types.js';
import { EVERYTHING_CONTEXT } from '../../constants.js';
import { getContextKeyFromMessage } from '../../utils/context.js';
import { useQueryResourceDetail } from '../../../resources/queries/useQueryResourceDetail.js';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { ResourcesPreviewUiConfigProvider } from '../../../resources/components/ResourcePreview/resourcePreviewUiConfigContext.js';
import { cssVar } from '@fabric-app/ui/theme/variables';

const MessageListScrollArea = styled(ScrollArea.Default).withConfig({
  displayName: "MessageList__MessageListScrollArea",
  componentId: "sc-52302aa4-0"
})([
  `width:100%;margin:0 auto;& > [data-radix-scroll-area-viewport] > div{display:block !important;}--scroll-area-fade-bg-color:`,
  `;`
], cssVar["color-bg-assistant"]);
const ContextChangeTag = ({ context }) => {
  const { subType, value, type } = context;
  const resourceQueryEnabled = subType === AssistantContextSubType.File && type !== AssistantContextType.Selection;
  const { resource: resourceData } = useQueryResourceDetail(Array.isArray(value) ? value[0] : void 0, {
    enabled: resourceQueryEnabled
  });
  const resource = resourceQueryEnabled ? resourceData : null;
  const resourceTitle = resource ? resource.name : null;
  const name = context.name ?? resourceTitle;
  const displayedName = name ?? context.subType;
  return /* @__PURE__ */ jsx(Tooltip, {
    label: name,
    children: /* @__PURE__ */ jsxs(Conversation.InfoTag, {
      children: [
        "Context changed to ",
        displayedName
      ]
    })
  });
};
const MessagesList = ({ children, style }) => {
  const listRef = useRef(null);
  const { messages, isLoading, isStreamingAnswer, streamingAnswer, activeContext } = useChatbot();
  useClientLayoutEffect(() => {
    const performScroll = () => {
      const scrollDiv = listRef.current?.querySelector("[data-radix-scroll-area-viewport]");
      if (scrollDiv) scrollDiv.scrollTop = scrollDiv.scrollHeight;
    };
    performScroll();
  }, [
    messages,
    streamingAnswer,
    isStreamingAnswer,
    isLoading,
    activeContext
  ]);
  const messagesByContext = useMemo(() => {
    let lastContextKey = void 0;
    return messages?.reduce((acc, message) => {
      const contextKey = getContextKeyFromMessage(message);
      if (!lastContextKey || contextKey !== lastContextKey) {
        acc.push({
          first: acc.length === 0,
          context: message.context ?? EVERYTHING_CONTEXT,
          action: message.action,
          messages: []
        });
        lastContextKey = contextKey;
      }
      acc[acc.length - 1].messages.push(message);
      return acc;
    }, []);
  }, [
    messages
  ]);
  const lastMessageContextKey = getContextKeyFromMessage(messages[messages.length - 1]);
  const isStreamingContextDifferent = streamingAnswer && getContextKeyFromMessage(streamingAnswer) !== lastMessageContextKey;
  return /* @__PURE__ */ jsx(MessageListScrollArea, {
    ref: listRef,
    scrollbarVariant: "subtle",
    "data-assistant-message-list": true,
    fade: "end",
    style: {
      flexGrow: 1
    },
    expandVertically: true,
    children: /* @__PURE__ */ jsx(ResourcesPreviewUiConfigProvider, {
      uiContext: "assistant",
      children: /* @__PURE__ */ jsxs(Conversation.List, {
        style,
        children: [
          messagesByContext.map(({ context, messages: messages2, action, first }, i) => /* @__PURE__ */ jsxs(React__default.Fragment, {
            children: [
              !first && /* @__PURE__ */ jsx(ContextChangeTag, {
                context,
                action
              }),
              messages2.map((message) => /* @__PURE__ */ jsx(ChatbotMessage, {
                message
              }, message.id))
            ]
          }, `${context.subType}-${context.value}-${i}`)),
          isStreamingContextDifferent && streamingAnswer && streamingAnswer.context && /* @__PURE__ */ jsx(ContextChangeTag, {
            context: streamingAnswer.context,
            action: streamingAnswer.action
          }),
          isStreamingAnswer && streamingAnswer && /* @__PURE__ */ jsx(ChatbotMessage, {
            message: streamingAnswer
          }),
          isLoading && /* @__PURE__ */ jsx(Fragment, {
            children: /* @__PURE__ */ jsxs(Loading, {
              children: [
                /* @__PURE__ */ jsx(FabricChatbotAvatar, {}),
                /* @__PURE__ */ jsx(LoadingDots, {
                  dotSize: 8
                })
              ]
            })
          }),
          /* @__PURE__ */ jsx(ChatbotError, {}),
          children
        ]
      })
    })
  });
};
const Loading = styled.div.withConfig({
  displayName: "MessageList__Loading",
  componentId: "sc-52302aa4-1"
})([
  `display:flex;width:100%;justify-content:flex-start;align-items:center;margin-bottom:16px;padding:0.625rem;gap:0.75rem;`
]);
const LoadingDots = styled(TypingDots).withConfig({
  displayName: "MessageList__LoadingDots",
  componentId: "sc-52302aa4-2"
})([
  `margin-left:8px;color:`,
  `;`
], cssVar["color-text-tertiary"]);

export { MessagesList as default };
//# sourceMappingURL=MessageList.js.map
